import { supabase } from '../lib/supabaseClient'
import { triggerEmail } from './emailTriggerService'
/**
 * Delete all user data and account
 */
export async function deleteUserAccount(userId: string): Promise<void> {
  try {
    // Get user info before deletion for email
    const {
      data: { user },
    } = await supabase.auth.getUser()
    const userEmail = user?.email
    const userName =
      user?.user_metadata?.firstName || user?.user_metadata?.fullName || 'User'
    // 1. Delete all analysis results
    const { error: analysisError } = await supabase
      .from('analysis_results')
      .delete()
      .eq('user_id', userId)
    if (analysisError) {
      console.error('Error deleting analysis results:', analysisError)
      throw new Error('Failed to delete analysis results')
    }
    // 2. Delete all uploaded screenshots from storage
    const { data: files, error: listError } = await supabase.storage
      .from('screenshots')
      .list(userId)
    if (listError) {
      console.error('Error listing screenshots:', listError)
      // Continue even if listing fails
    }
    if (files && files.length > 0) {
      const filePaths = files.map((file) => `${userId}/${file.name}`)
      const { error: deleteFilesError } = await supabase.storage
        .from('screenshots')
        .remove(filePaths)
      if (deleteFilesError) {
        console.error('Error deleting screenshots:', deleteFilesError)
        // Continue even if file deletion fails
      }
    }
    // 3. Queue deletion confirmation email via email_logs table
    if (userEmail) {
      try {
        await triggerEmail(
          'accountDeletion',
          { deletionDate: new Date().toISOString() },
          userEmail,
          userName,
        )
      } catch (emailError) {
        console.error('Error queuing deletion email:', emailError)
        // Continue even if email queue fails
      }
    }
    // 4. Delete the user account from Supabase Auth
    // Note: This will cascade delete related data
    const { error: deleteUserError } =
      await supabase.auth.admin.deleteUser(userId)
    if (deleteUserError) {
      console.error('Error deleting user account:', deleteUserError)
      throw new Error('Failed to delete user account')
    }
    // 5. Sign out the user
    await supabase.auth.signOut()
  } catch (error: any) {
    console.error('Account deletion error:', error)
    throw new Error(error.message || 'Failed to delete account')
  }
}
/**
 * Check if user has active subscription before deletion
 */
export async function checkActiveSubscription(
  userId: string,
): Promise<boolean> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    const subscriptionPlan = user?.user_metadata?.subscriptionPlan || 'free'
    // Any plan other than the free tier represents a paid commitment.
    return subscriptionPlan !== 'free'
  } catch (error) {
    console.error('Error checking subscription:', error)
    return false
  }
}
